import { DetailsView } from "@components/common/DetailsView";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { fields } from "./Fields";
import { Agency } from "@gen/agency/v1/agency_pb";
import { getAgencies } from "@services/agency";
import { getCaisses } from "@services/caisse";
import { ConnectError } from "@connectrpc/connect";
import { useToaster } from "@hooks/useToaster";
import { Caisse } from "@gen/caisse/v1/caisse_pb";
import { User } from "@gen/user/v1/user_pb";
import { getUser, updateUser } from "@services/user";
import { useUserStore } from "@stores/userStore";

export function UserDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [user, setUser] = useState<User>();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [caisses, setCaisses] = useState<Caisse[]>([]);
  const { addToast } = useToaster();
  const { user: connectedUser } = useUserStore();

  async function fetchUser() {
    if (!id) {
      return;
    }
    try {
      const user = await getUser(id);
      setUser(user);
    } catch (e: unknown) {
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    }
  }

  async function submitUser(user: User) {
    try {
      await updateUser(user);
      addToast("info", t("admin.user.userUpdated"));
      fetchUser();
    } catch (e) {
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    }
  }

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    fetchUser();
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        setAgencies((await getAgencies(1, 100)).agencies);
        setCaisses((await getCaisses(1, 100)).caisses);
      } catch (error) {
        console.error("Error fetching agencies or caisses:", error);
      }
    })();
  }, []);

  const isLoading =
    !user ||
    !agencies ||
    agencies.length == 0 ||
    !caisses ||
    caisses.length == 0;

  return (
    <DetailsView
      item={user}
      title={`${user?.firstName} ${user?.lastName}`}
      fields={fields(t, agencies, caisses)}
      isEditable={!!connectedUser && !!user && user.role <= connectedUser.role}
      isLoading={isLoading}
      onSubmit={submitUser}
    />
  );
}
